import { ReactNode, useCallback, useEffect, useMemo } from 'react'

import { SelectChangeEvent } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { setSelectedRegion } from 'redux/features/emailServer/emailServerSlice'
import { isPending } from 'redux/toolkit/api'
import { CountryData, getCountryFromCode2, getFilteredCountryList } from 'lib/isoCountries'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'
import { trackEventInAllServices, TRACKING_EVENTS } from 'lib/monitoring/monitoringService'

export interface State {
  countries: CountryData[]
  selectedRegion: CountryData | undefined
  isSetSelectedRegionInProgress: boolean
  selectedRegionError: boolean
  prepopulatedRegion?: string | null
}

export interface EventHandlers {
  onSelectRegion: (event: SelectChangeEvent<unknown>, child: ReactNode) => void
}

export type RegionSelectionLogic = [State, EventHandlers]

export const useRegionSelectionLogic = (): RegionSelectionLogic => {
  const dispatch = useAppDispatch()
  const { isSetSelectedRegionInProgress, selectedRegion, selectedRegionError, prepopulatedRegion } = useAppSelector(
    _store => ({
      user: _store.auth.accessTokenObject,
      accessToken: _store.auth.accessToken,
      isSetSelectedRegionInProgress: isPending(_store.emailServer.setRegionApiStatus),
      selectedRegion: _store.emailServer.selectedRegion,
      selectedRegionError: _store.emailServer.selectedRegionError,
      prepopulatedRegion: _store.emailServer.emailServerSettings?.region
    })
  )
  const VALID_COUNTRIES = isMyFeatureOn(FEATURES.EGD_React_India_Deployment)
    ? ['US', 'GB', 'DE', 'CA', 'AU', 'IN']
    : ['US', 'GB', 'DE', 'CA', 'AU']
  const countries = getFilteredCountryList(VALID_COUNTRIES)

  // init
  useEffect(
    () => {
      trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_2_START)
      if (prepopulatedRegion) {
        const region = prepopulatedRegion.replace('UK', 'GB')
        const preselectedCountry = countries.find(country => country.code === region)
        if (preselectedCountry) {
          dispatch(setSelectedRegion(preselectedCountry))
        }
      }
    },
    // eslint-disable-next-line
    []
  )

  const onSelectRegion: EventHandlers['onSelectRegion'] = useCallback(
    e => {
      const newRegion = getCountryFromCode2((e.target.value || '') as string)

      if (newRegion) {
        trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_2_REGION_CHANGE, { selectedRegion: newRegion })
        dispatch(setSelectedRegion(newRegion))
      }
    },
    [dispatch]
  )

  return useMemo(
    () => [
      {
        countries,
        selectedRegion,
        isSetSelectedRegionInProgress,
        selectedRegionError,
        prepopulatedRegion
      },
      {
        onSelectRegion
      }
    ],
    [selectedRegion, onSelectRegion, countries, isSetSelectedRegionInProgress, selectedRegionError, prepopulatedRegion]
  )
}
